import { Runtime } from "fp";

import React from "react";

import * as ErrorMessage from "~/ErrorMessage";
import { HoursOfService } from "~/HoursOfService";
import { NotApplicable } from "~/NotApplicable";
import { Search } from "~/Search";
import { Time } from "~/Time";

export type Timer = { readonly timer: Time.Duration };
export function Timer(timer: Timer) {
  return Timer.isOK(timer) ? (
    <>{Timer.toString(timer)}</>
  ) : (
    <ErrorMessage.Component danger icon>
      {Timer.toString(timer)}
    </ErrorMessage.Component>
  );
}

export namespace Timer {
  export const isOK = ({ timer }: Timer): boolean => timer.duration > 0;

  export const toString = ({ timer }: Timer): string =>
    Time.Duration.toHHMM(timer);

  export const toSearch: Search.From<Timer> = ({ timer }) =>
    Time.Duration.toSearch(timer);

  export function Break({ hoursOfService }: HoursOfService) {
    return Break.isApplicable({ hoursOfService }) &&
      hoursOfService.timers?.break ? (
      <Timer {...hoursOfService.timers?.break} />
    ) : (
      <NotApplicable />
    );
  }

  export namespace Break {
    export const isOK = (
      hoursOfService: HoursOfService,
      breakTimer: Timer
    ): boolean => !isApplicable(hoursOfService) || Timer.isOK(breakTimer);

    export const isApplicable = ({ hoursOfService }: HoursOfService): boolean =>
      !!hoursOfService.type &&
      !!hoursOfService.exemption &&
      (HoursOfService.Type.hasBreak({ type: hoursOfService.type }) ||
        HoursOfService.Exemption.hasBreak({
          exemption: hoursOfService.exemption,
        }));
  }

  export namespace Response {
    export const runtime = () => Time.Duration.Response.runtime();
  }
}

export type Timers = {
  readonly timers: {
    readonly cycle: Timer;
    readonly shift: Timer;
    readonly drive: Timer;
    readonly break: Timer;
  };
};

export namespace Timers {
  export const toSearch: Search.From<Timers> = ({ timers }) =>
    Search.join(
      Timer.toSearch(timers.cycle),
      Timer.toSearch(timers.shift),
      Timer.toSearch(timers.drive),
      Timer.toSearch(timers.break)
    );

  export const isOK = ({ hoursOfService }: HoursOfService): boolean =>
    !!hoursOfService.timers &&
    !!hoursOfService.type &&
    Timer.isOK(hoursOfService.timers.cycle) &&
    Timer.isOK(hoursOfService.timers.shift) &&
    Timer.isOK(hoursOfService.timers.drive) &&
    (!Timer.Break.isApplicable({ hoursOfService }) ||
      Timer.isOK(hoursOfService.timers.break));

  export namespace Response {
    export const runtime = () =>
      Runtime.strict({
        timer_cycle: Timer.Response.runtime(),
        timer_shift: Timer.Response.runtime(),
        timer_drive: Timer.Response.runtime(),
        timer_nobreak: Timer.Response.runtime(),
      });

    export const decode = (
      response: Runtime.ToType<typeof runtime>
    ): Timers => ({
      timers: {
        cycle: { timer: response.timer_cycle },
        shift: { timer: response.timer_shift },
        drive: { timer: response.timer_drive },
        break: { timer: response.timer_nobreak },
      },
    });
  }
}
